import { jitterMs } from "./performance-realtime.helpers";
import { RefreshTier, ResourceKey } from "./performance-realtime.model";

/** Ejecuta la recarga acotada de una clave y devuelve cuándo terminó. */
export type SchedulerRefresh = (key: ResourceKey) => Promise<unknown>;

export interface PerformanceRefreshSchedulerOptions {
	refresh: SchedulerRefresh;
	/** Inyectable: en producción `Math.random`, en pruebas un valor fijo (design.md §8.1). */
	random?: () => number;
	/** Interruptor de emergencia `REALTIME_REFETCH_ENABLED` (design.md §8.5). */
	isEnabled?: () => boolean;
	/** Ventana de agrupación desde el último evento de la clave (design.md §8.2). */
	wait?: number;
	/** Techo desde el primer evento pendiente de la clave (design.md §8.2). */
	maxWait?: number;
	now?: () => number;
	setTimeoutFn?: (handler: () => void, ms: number) => unknown;
	clearTimeoutFn?: (id: unknown) => void;
}

interface PendingKeyState {
	tier: RefreshTier;
	/** Instante del primer evento pendiente de esta clave, para el techo. */
	firstEventAt: number;
	/** Instante en el que se cumple el jitter aplicado al entrar en pendiente. */
	dueAfterJitterAt: number;
	timer: unknown;
}

const TIER_WEIGHT: Record<RefreshTier, number> = { personal: 0, process: 1, workspace: 2 };

function widestTier(a: RefreshTier, b: RefreshTier): RefreshTier {
	return TIER_WEIGHT[b] > TIER_WEIGHT[a] ? b : a;
}

/**
 * Planificador de recargas acotadas por recurso, sin Angular (design.md §8).
 * Aplica jitter una vez por clave al entrar en pendiente, agrupa ráfagas con
 * un techo (mismo criterio que `debounceMax()` del gestor,
 * `src/utils/realtime.ts:94`), mantiene una sola petición en vuelo por clave,
 * y respeta el segundo plano, la reconexión y el interruptor de emergencia.
 */
export class PerformanceRefreshScheduler {
	private readonly refresh: SchedulerRefresh;
	private readonly random: () => number;
	private readonly isEnabled: () => boolean;
	private readonly wait: number;
	private readonly maxWait: number;
	private readonly now: () => number;
	private readonly setTimeoutFn: (handler: () => void, ms: number) => unknown;
	private readonly clearTimeoutFn: (id: unknown) => void;

	/** Claves en agrupación, con temporizador activo. */
	private readonly pending = new Map<ResourceKey, PendingKeyState>();
	/** Claves ensuciadas mientras la app está en segundo plano, sin temporizador. */
	private readonly backgroundPending = new Map<ResourceKey, RefreshTier>();
	/** Claves con una petición en vuelo ahora mismo. */
	private readonly inFlight = new Set<ResourceKey>();
	/** Claves ensuciadas mientras su petición seguía en vuelo. */
	private readonly dirtyDuringFlight = new Map<ResourceKey, RefreshTier>();
	private background = false;

	constructor(options: PerformanceRefreshSchedulerOptions) {
		this.refresh = options.refresh;
		this.random = options.random ?? Math.random;
		this.isEnabled = options.isEnabled ?? (() => true);
		this.wait = options.wait ?? 2000;
		this.maxWait = options.maxWait ?? 10000;
		this.now = options.now ?? (() => Date.now());
		this.setTimeoutFn = options.setTimeoutFn ?? ((handler, ms) => setTimeout(handler, ms));
		this.clearTimeoutFn = options.clearTimeoutFn ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
	}

	/** Un evento de tiempo real afecta a `key`, con la ventana de jitter de `tier`. */
	notify(key: ResourceKey, tier: RefreshTier): void {
		if (!this.isEnabled()) return;

		if (this.inFlight.has(key)) {
			const existing = this.dirtyDuringFlight.get(key);
			this.dirtyDuringFlight.set(key, existing ? widestTier(existing, tier) : tier);
			return;
		}

		if (this.background) {
			const existing = this.backgroundPending.get(key);
			this.backgroundPending.set(key, existing ? widestTier(existing, tier) : tier);
			return;
		}

		this.armTimer(key, tier);
	}

	/** true = la app pasa a segundo plano; false = vuelve a primer plano (design.md §8.3). */
	setBackground(background: boolean): void {
		const wasBackground = this.background;
		this.background = background;

		if (wasBackground && !background) {
			const resumed = Array.from(this.backgroundPending.entries());
			this.backgroundPending.clear();
			for (const [key, tier] of resumed) {
				this.armTimer(key, tier);
			}
		}
	}

	/**
	 * Reconexión del socket: solo las claves con un registro visible en ese
	 * momento, con la ventana de workspace (design.md §8.4). `visibleKeys` la
	 * decide quien registra las pantallas (`PerformanceRealtimeService`, A2).
	 */
	reconnect(visibleKeys: ResourceKey[]): void {
		if (!this.isEnabled()) return;
		for (const key of visibleKeys) {
			this.notify(key, "workspace");
		}
	}

	private armTimer(key: ResourceKey, tier: RefreshTier): void {
		const now = this.now();
		let state = this.pending.get(key);

		if (!state) {
			state = { tier, firstEventAt: now, dueAfterJitterAt: now + jitterMs(tier, this.random), timer: null };
			this.pending.set(key, state);
		} else {
			state.tier = widestTier(state.tier, tier);
		}

		if (state.timer !== null) this.clearTimeoutFn(state.timer);

		const untilCeiling = Math.max(0, state.firstEventAt + this.maxWait - now);
		const untilJitter = Math.max(0, state.dueAfterJitterAt - now);
		const delay = Math.max(untilJitter, Math.min(this.wait, untilCeiling));

		state.timer = this.setTimeoutFn(() => this.fire(key), delay);
	}

	private fire(key: ResourceKey): void {
		this.pending.delete(key);
		this.inFlight.add(key);

		this.refresh(key)
			.catch(() => undefined)
			.then(() => {
				this.inFlight.delete(key);
				const dirtyTier = this.dirtyDuringFlight.get(key);
				if (dirtyTier) {
					this.dirtyDuringFlight.delete(key);
					this.notify(key, dirtyTier);
				}
			});
	}
}
